import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Center, Flex, Icon, Spinner, Text, } from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { useApplicableOffers } from "../../hooks/useApplicableOffers";
import InHouseOfferPreviewCard from "./InHouseOfferPreviewCard";
import InHouseOffersEmptyState from "./InHouseOffersEmptyState";
import InHouseOfferDetailsSheet from "./InHouseOfferDetailsSheet";
const DEFAULT_TEXT = "white";
const DEFAULT_PRIMARY = "#8155FE";
const DEFAULT_PRIMARY_HOVER = "#9166FF";
const InHouseOffersPreview = ({ eventId, reservationTime, onAllOffersClick, previewLimit = 5, hideWhenEmpty = false, pubColors, }) => {
    var _a, _b, _c;
    const textColor = (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _a !== void 0 ? _a : DEFAULT_TEXT;
    const primaryColor = (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _b !== void 0 ? _b : DEFAULT_PRIMARY;
    const primaryHoverColor = (_c = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primaryHover) !== null && _c !== void 0 ? _c : DEFAULT_PRIMARY_HOVER;
    const { data, isLoading, isError } = useApplicableOffers({
        eventId,
        reservationTime,
    });
    const offers = data !== null && data !== void 0 ? data : [];
    const visibleOffers = offers.slice(0, previewLimit);
    const [activeIndex, setActiveIndex] = useState(0);
    const [desktopActiveIndex, setDesktopActiveIndex] = useState(0);
    const [detailsOffer, setDetailsOffer] = useState(null);
    const closeDetails = () => {
        setDetailsOffer(null);
    };
    const scrollerRef = useRef(null);
    const desktopScrollerRef = useRef(null);
    const pauseUntilRef = useRef(0);
    const desktopPauseUntilRef = useRef(0);
    const desktopPageCount = Math.ceil(visibleOffers.length / 2);
    // Mobile scroll tracking
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el)
            return;
        const onScroll = () => {
            const w = el.clientWidth;
            if (w === 0)
                return;
            const idx = Math.round(el.scrollLeft / w);
            setActiveIndex(idx);
        };
        const onUserInteract = () => {
            pauseUntilRef.current = Date.now() + 8000;
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        el.addEventListener("touchstart", onUserInteract, { passive: true });
        el.addEventListener("pointerdown", onUserInteract, { passive: true });
        return () => {
            el.removeEventListener("scroll", onScroll);
            el.removeEventListener("touchstart", onUserInteract);
            el.removeEventListener("pointerdown", onUserInteract);
        };
    }, [visibleOffers.length]);
    // Desktop scroll tracking
    useEffect(() => {
        const el = desktopScrollerRef.current;
        if (!el)
            return;
        const onScroll = () => {
            const w = el.clientWidth;
            if (w === 0)
                return;
            const idx = Math.round(el.scrollLeft / w);
            setDesktopActiveIndex(idx);
        };
        const onUserInteract = () => {
            desktopPauseUntilRef.current = Date.now() + 8000;
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        el.addEventListener("touchstart", onUserInteract, { passive: true });
        el.addEventListener("pointerdown", onUserInteract, { passive: true });
        return () => {
            el.removeEventListener("scroll", onScroll);
            el.removeEventListener("touchstart", onUserInteract);
            el.removeEventListener("pointerdown", onUserInteract);
        };
    }, [visibleOffers.length]);
    // Mobile auto-scroll
    useEffect(() => {
        if (visibleOffers.length <= 1)
            return;
        if (detailsOffer)
            return;
        const id = window.setInterval(() => {
            if (Date.now() < pauseUntilRef.current)
                return;
            const el = scrollerRef.current;
            if (!el)
                return;
            const w = el.clientWidth;
            if (w === 0)
                return;
            const current = Math.round(el.scrollLeft / w);
            const next = (current + 1) % visibleOffers.length;
            el.scrollTo({ left: next * w, behavior: "smooth" });
        }, 4000);
        return () => window.clearInterval(id);
    }, [visibleOffers.length, detailsOffer]);
    // Desktop auto-scroll (pages of 2)
    useEffect(() => {
        if (desktopPageCount <= 1)
            return;
        if (detailsOffer)
            return;
        const id = window.setInterval(() => {
            if (Date.now() < desktopPauseUntilRef.current)
                return;
            const el = desktopScrollerRef.current;
            if (!el)
                return;
            const w = el.clientWidth;
            if (w === 0)
                return;
            const current = Math.round(el.scrollLeft / w);
            const next = (current + 1) % desktopPageCount;
            el.scrollTo({ left: next * w, behavior: "smooth" });
        }, 4000);
        return () => window.clearInterval(id);
    }, [desktopPageCount, detailsOffer]);
    const isEmpty = !isLoading && !isError && offers.length === 0;
    const noEvent = !eventId || !reservationTime;
    if (hideWhenEmpty && (noEvent || isEmpty || isError)) {
        return null;
    }
    return (_jsxs(Box, { w: "100%", children: [_jsxs(Flex, { align: ["flex-start", "flex-start", "center"], justify: "space-between", gap: 4, mb: 5, flexDir: ["row"], children: [_jsxs(Box, { children: [_jsxs(Text, { fontSize: ["30px", "30px", "35px", "40px"], fontWeight: 500, lineHeight: "40px", color: textColor, textTransform: "uppercase", children: ["In-House", _jsx("br", {}), "Offers"] }), _jsx(Text, { fontSize: "10px", fontWeight: 400, lineHeight: "18px", color: textColor, opacity: 0.6, mt: 2, textTransform: "capitalize", children: "Reserve Online, Enjoy Complimentary & Discounts" })] }), _jsx(Button, { bg: primaryColor, color: "white", borderRadius: "10px", py: "10px", px: "12px", h: "auto", fontSize: "12px", fontWeight: 500, lineHeight: "24px", textTransform: "uppercase", rightIcon: _jsx(Icon, { as: ArrowForwardIcon, transform: "rotate(-45deg)", boxSize: 3.5 }), _hover: { bg: primaryHoverColor }, onClick: onAllOffersClick, children: "All Offers" })] }), noEvent ? null : isLoading ? (_jsx(Center, { py: 10, children: _jsx(Spinner, { color: primaryColor }) })) : isEmpty || isError ? (_jsx(InHouseOffersEmptyState, {})) : (_jsxs(_Fragment, { children: [_jsxs(Box, { display: ["block", "block", "none"], children: [_jsx(Box, { ref: scrollerRef, display: "flex", gap: 3, overflowX: "auto", scrollSnapType: "x mandatory", css: {
                                    "&::-webkit-scrollbar": { display: "none" },
                                    scrollbarWidth: "none",
                                }, children: visibleOffers.map((offer) => (_jsx(Box, { flex: "0 0 100%", scrollSnapAlign: "start", children: _jsx(InHouseOfferPreviewCard, { offer: offer, onDetailsClick: (o) => setDetailsOffer(o) }) }, offer.id))) }), visibleOffers.length > 1 && (_jsx(Flex, { justify: "center", gap: 2, mt: 4, children: visibleOffers.map((_, i) => (_jsx(Box, { w: i === activeIndex ? "10px" : "6px", h: "6px", borderRadius: "full", bg: textColor, opacity: i === activeIndex ? 1 : 0.3, transition: "all 0.2s" }, i))) }))] }), _jsx(Box, { display: ["none", "none", "block"], overflow: "hidden", w: "100%", children: visibleOffers.length === 1 ? (_jsx(Box, { maxW: "50%", children: _jsx(InHouseOfferPreviewCard, { offer: visibleOffers[0], onDetailsClick: (o) => setDetailsOffer(o) }) })) : (_jsxs(_Fragment, { children: [_jsx(Box, { ref: desktopScrollerRef, display: "flex", w: "100%", overflowX: "auto", css: {
                                        scrollSnapType: "x mandatory",
                                        "&::-webkit-scrollbar": { display: "none" },
                                        scrollbarWidth: "none",
                                    }, children: Array.from({ length: desktopPageCount }).map((_, pageIdx) => {
                                        const pageOffers = visibleOffers.slice(pageIdx * 2, pageIdx * 2 + 2);
                                        return (_jsxs(Flex, { flex: "0 0 100%", css: { scrollSnapAlign: "start" }, gap: 5, children: [pageOffers.map((offer) => (_jsx(Box, { flex: "1", minW: 0, children: _jsx(InHouseOfferPreviewCard, { offer: offer, onDetailsClick: (o) => setDetailsOffer(o) }) }, offer.id))), pageOffers.length === 1 && _jsx(Box, { flex: "1" })] }, pageIdx));
                                    }) }), desktopPageCount > 1 && (_jsx(Flex, { justify: "center", gap: 2, mt: 4, children: Array.from({ length: desktopPageCount }).map((_, i) => (_jsx(Box, { w: i === desktopActiveIndex ? "10px" : "6px", h: "6px", borderRadius: "full", bg: textColor, opacity: i === desktopActiveIndex ? 1 : 0.3, transition: "all 0.2s" }, i))) }))] })) })] })), _jsx(InHouseOfferDetailsSheet, { isOpen: !!detailsOffer, onClose: closeDetails, offer: detailsOffer })] }));
};
export default InHouseOffersPreview;
